import React from 'react';
import DataTable, { ConditionalStyles, TableProps, createTheme } from 'react-data-table-component';
import { ActivityIndicator, View } from 'react-native';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import Tooltip from './Tooltip';
import { MyCheckbox } from './MyCheckbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

export default function MyDataTable<T>(props: TableProps<T & { odd?: boolean; }> & {
    shortPadding?: boolean;
    tableFlex?: boolean;
    noResultsText?: string;
    useHighlightedHeader?: boolean;
    useMinimalTable?: boolean;
    conditionalRowStyle?: ConditionalStyles<T & { odd?: boolean; }>;
    disableSelectTooltip?: string | React.ReactNode;
    rowHeight?: number;
}) {
    const { styles, theme } = useStyles(stylesheet);

    createTheme('dark', {
        text: {
            primary: '#D3CFC9',
        },
        background: {
            default: '#181A1B',
        },
        context: {
            color: '#D3CFC9',
        }
    }, 'dark');

    if (typeof props.data[0] === 'object') {
        let index = 0;
        for (const row of props.data) {
            row.odd = index % 2 != 0;
            index++;
        }
    }

    function getConditionalRowStyles(): ConditionalStyles<T & { odd?: boolean; }>[] {
        const result: ConditionalStyles<T & { odd?: boolean; }>[] = [{
            when: row => (row.odd || false) && !props.useMinimalTable,
            style: {
                backgroundColor: theme.colors.dataTable.oddRowBackground,
                ...(props.pointerOnHover ? {
                    '&:hover': {
                        backgroundColor: theme.colors.dataTable.oddRowOnHoverBackground,
                    },
                } : {}),
            }
        }, {
            when: row => (!row.odd) && !props.useMinimalTable,
            style: {
                backgroundColor: theme.colors.dataTable.background,
                ...(props.pointerOnHover ? {
                    '&:hover': {
                        backgroundColor: theme.colors.dataTable.rowOnHoverBackground,
                    },
                } : {}),
            }
        }];

        if (props.conditionalRowStyle) {
            result.push(props.conditionalRowStyle);
        }

        return result;
    }

    return <DataTable
        {...props}
        fixedHeader={true}
        noDataComponent={<MyAppText style={styles.noDataComponent}>
            {props.noResultsText ?? translate('noResults')}
            {props.noResultsText && '.'}
        </ MyAppText>}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        selectableRowsComponent={(selectableRowProps: { name: string; checked: boolean; disabled: boolean; onClick: (val: boolean) => void; }) => {
            const displayTooltip = selectableRowProps.name !== 'select-all-rows' && selectableRowProps.disabled;
            const tooltipContent = props.disableSelectTooltip ? props.disableSelectTooltip : translate('disabled');

            return <View style={{ flex: 1, alignItems: 'center', zIndex: 1 }}>
                {displayTooltip ? <Tooltip
                    useSimpleBox
                    style={{
                        width: 'auto',
                        color: theme.colors.white['500'],
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 16,
                        paddingRight: 16,
                        borderRadius: 12,
                        justifyContent: 'center',
                        zIndex: 9999,
                        fontSize: 12,
                        fontWeight: 500,
                        top: -8,
                        background: theme.colors.neutral['500'],
                    }}
                    tooltipContent={tooltipContent}
                    tooltipDirection='right'
                >
                    <MyCheckbox
                        label=''
                        style={{ columnGap: 0 }}
                        checked={selectableRowProps.checked}
                        disabled={selectableRowProps.disabled}
                        setChecked={(val => {
                            selectableRowProps.onClick(val);
                        })}
                        customIcon={<FontAwesomeIcon icon={faCheck} fontSize={10} color={theme.colors.white['500']} />}
                        checkBoxStyle={{
                            backgroundColor: selectableRowProps.checked
                                ? selectableRowProps.disabled ? theme.colors.primary['200'] : theme.colors.primary['500']
                                : selectableRowProps.disabled ? theme.colors.neutral['200'] : 'transparent',
                            borderWidth: 1,
                            borderColor: theme.colors.neutral['300']
                        }}
                    />
                </Tooltip>
                    : <MyCheckbox
                        label=''
                        style={{ columnGap: 0 }}
                        checked={selectableRowProps.checked}
                        disabled={selectableRowProps.disabled}
                        setChecked={(val => {
                            selectableRowProps.onClick(val);
                        })}
                        customIcon={<FontAwesomeIcon icon={faCheck} fontSize={10} color={theme.colors.white['500']} />}
                        checkBoxStyle={{
                            backgroundColor: selectableRowProps.checked
                                ? selectableRowProps.disabled ? theme.colors.primary['200'] : theme.colors.primary['500']
                                : selectableRowProps.disabled ? theme.colors.neutral['200'] : 'transparent',
                            borderWidth: 1,
                            borderColor: theme.colors.neutral['300']
                        }}
                    />
                }
            </View>;
        }}
        customStyles={{
            rows: {
                style: styles.rows(props.rowHeight),
            },
            head: {
                style: {
                    zIndex: 2,
                }
            },
            headCells: {
                style: props.useHighlightedHeader
                    ? styles.headCellsHighlighted(props.rowHeight)
                    : styles.headCells(props.rowHeight),
            },
            cells: {
                style: styles.cells(props.shortPadding)
            },
            table: {
                style: props.tableFlex ? { flex: 1 } : props.useMinimalTable ? styles.minimalTable : {}
            },
            tableWrapper: {
                style: props.tableFlex ? { display: 'flex', flex: 1 } : {}
            },
            responsiveWrapper: {
                style: props.tableFlex ? { display: 'flex', flex: 1 } : { height: '100%' }
            },
            headRow: {
                style: props.useHighlightedHeader
                    ? styles.headCellsHighlighted(props.rowHeight)
                    : styles.headCells(props.rowHeight),
            },
        }}
        progressComponent={<View style={{ paddingTop: 20 }}>
            <ActivityIndicator size='small' color={theme.colors.dataTable.loading} />
        </View>}
        conditionalRowStyles={getConditionalRowStyles()}
        theme={UnistylesRuntime.themeName}
    />;
}

const stylesheet = createStyleSheet(theme => ({
    noDataComponent: {
        color: theme.colors.dataTable.noDataMessage,
        paddingTop: 10,
    },
    rows: (rowHeight?: number) => ({
        fontFamily: 'Open Sans',
        fontSize: 14,
        ...(rowHeight ? {
            minHeight: rowHeight ? rowHeight : undefined,
        } : {})

    }),
    headCells: (rowHeight?: number) => ({
        fontFamily: 'Open Sans',
        fontSize: 13,
        fontWeight: '600',
        ...(rowHeight ? {
            minHeight: rowHeight ? rowHeight : undefined,
        } : {})
    }),
    headCellsHighlighted: (rowHeight?: number) => ({
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.paginationBar.background,
        fontSize: 12,
        fontWeight: '600',
        ...(rowHeight ? {
            minHeight: rowHeight ? rowHeight : undefined,
        } : {})
    }),
    minimalTable: {
        borderRadius: 4,
        overflow: 'hidden',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.colors.container.border
    },
    cells: (horizontalPadding?: boolean) => ({
        paddingLeft: horizontalPadding ? 5 : 16,
        paddingRight: horizontalPadding ? 5 : 16,
    })
}));
